import React, { useEffect, useMemo, useState } from "react";
import feedingService from "../../../shared/api/animal/feedingService";
import inventoryService from "../../../shared/api/animal/inventoryService";

const SPECIES_OPTIONS = ["GOAT", "SHEEP", "CATTLE", "CAMEL"];
const PROJECT_TYPE_OPTIONS = ["FATTENING", "BREEDING", "DAIRY"];

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function apiMsg(e) {
  return (
    feedingService?._apiMsg?.(e) ||
    e?.response?.data?.message ||
    e?.response?.data?.error ||
    (typeof e?.response?.data === "string" ? e.response.data : null) ||
    e?.message ||
    "Request failed"
  );
}

function formatDate(v) {
  return v ? String(v).slice(0, 10) : "-";
}

function titleCase(value) {
  return String(value || "-")
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function statusStyle(status) {
  const map = {
    PENDING: { background: "rgba(234,179,8,0.18)", color: "#fde68a", border: "1px solid rgba(234,179,8,0.35)" },
    GIVEN: { background: "rgba(34,197,94,0.18)", color: "#86efac", border: "1px solid rgba(34,197,94,0.35)" },
    NOT_GIVEN: { background: "rgba(239,68,68,0.18)", color: "#fca5a5", border: "1px solid rgba(239,68,68,0.35)" },
    SKIPPED: { background: "rgba(148,163,184,0.18)", color: "#cbd5e1", border: "1px solid rgba(148,163,184,0.35)" },
    RESCHEDULED: { background: "rgba(59,130,246,0.18)", color: "#93c5fd", border: "1px solid rgba(59,130,246,0.35)" },
  };
  return map[status] || { background: "rgba(148,163,184,0.18)", color: "#cbd5e1", border: "1px solid rgba(148,163,184,0.35)" };
}

function Badge({ status }) {
  return <span style={{ ...styles.badge, ...statusStyle(status) }}>{status || "-"}</span>;
}

function Stepper({ step }) {
  return (
    <div style={styles.stepper}>
      <div style={styles.stepItem}>
        <div style={{ ...styles.stepCircle, ...(step >= 1 ? styles.stepActive : {}) }}>{step > 1 ? "✓" : "1"}</div>
        <div style={{ ...styles.stepLabel, ...(step >= 1 ? styles.stepLabelActive : {}) }}>Feeding rule</div>
      </div>
      <div style={{ ...styles.stepLine, ...(step > 1 ? styles.stepLineDone : {}) }} />
      <div style={styles.stepItem}>
        <div style={{ ...styles.stepCircle, ...(step === 2 ? styles.stepActive : {}) }}>2</div>
        <div style={{ ...styles.stepLabel, ...(step === 2 ? styles.stepLabelActive : {}) }}>Review & confirm</div>
      </div>
    </div>
  );
}

export default function FeedingManagementPage() {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [inventory, setInventory] = useState([]);
  const [rules, setRules] = useState([]);
  const [step, setStep] = useState(1);
  const [editingRuleId, setEditingRuleId] = useState(null);
  const [ruleForm, setRuleForm] = useState({
    species: "GOAT",
    projectType: "FATTENING",
    minWeightKg: "",
    maxWeightKg: "",
    gramsPerKgPerDay: "",
    feedingTimesPerDay: 3,
    feedInventoryId: "",
    components: [{ inventoryId: "", percentage: 100 }],
    startDate: todayIso(),
    active: true,
    notes: "",
  });
  const [previewWeight, setPreviewWeight] = useState("15");

  const feedInventoryOptions = useMemo(() => {
    const items = Array.isArray(inventory) ? inventory : [];
    return items.filter((item) => {
      const category = String(item.itemCategory || item.type || "").toUpperCase();
      return !category || category === "FEED";
    });
  }, [inventory]);

  const rationPercentageTotal = (ruleForm.components || []).reduce((total, c) => total + Number(c.percentage || 0), 0);
  const previewTotalGrams = Math.round(Number(previewWeight || 0) * Number(ruleForm.gramsPerKgPerDay || 0) * 1000) / 1000;
  const estimatedDailyCost = (ruleForm.components || []).reduce((sum, c) => {
    const feed = feedInventoryOptions.find(item => String(item.id) === String(c.inventoryId));
    const unit = String(feed?.unit || "").toUpperCase();
    const qty = unit === "KG" ? 1000 : unit === "G" ? 1 : null;
    const price = feed?.averageUnitCost ?? feed?.unitCost;
    if (!qty || price == null) return sum;
    return sum + (previewTotalGrams * Number(c.percentage || 0) / 100 / qty) * Number(price);
  }, 0);

  useEffect(() => {
    loadInitial();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadInitial() {
    try {
      setLoading(true);
      setError("");
      const [invData, ruleData] = await Promise.all([
        inventoryService.getAllInventory(),
        feedingService.listRules(),
      ]);
      setInventory(Array.isArray(invData) ? invData : []);
      setRules(Array.isArray(ruleData) ? ruleData : []);
    } catch (e) {
      setError(apiMsg(e));
    } finally {
      setLoading(false);
    }
  }

  async function refreshRules() {
    const data = await feedingService.listRules();
    setRules(Array.isArray(data) ? data : []);
  }

  function clearRuleForm() {
    setEditingRuleId(null);
    setStep(1);
    setRuleForm({
      species: "GOAT",
      projectType: "FATTENING",
      minWeightKg: "",
      maxWeightKg: "",
      gramsPerKgPerDay: "",
      feedingTimesPerDay: 3,
      feedInventoryId: "",
      components: [{ inventoryId: "", percentage: 100 }],
      startDate: todayIso(),
      active: true,
      notes: "",
    });
  }

  function validateRule() {
    if (!ruleForm.species) return "Select species.";
    if (!ruleForm.projectType) return "Select project type.";
    if (!ruleForm.gramsPerKgPerDay || Number(ruleForm.gramsPerKgPerDay) <= 0) return "Daily feed rate must be greater than 0.";
    if (!ruleForm.components?.length || ruleForm.components.some(c => !c.inventoryId || !(Number(c.percentage)>0))) return "Choose a feed and a positive percentage in each row.";
    if (new Set(ruleForm.components.map(c => String(c.inventoryId))).size !== ruleForm.components.length) return "The same feed cannot appear twice.";
    if (Math.abs(ruleForm.components.reduce((sum,c)=>sum+Number(c.percentage),0)-100)>0.000001) return "Feed percentages must total 100%.";
    if (!ruleForm.startDate) return "Select feeding start date.";
    if (ruleForm.minWeightKg !== "" && ruleForm.maxWeightKg !== "" && Number(ruleForm.minWeightKg) > Number(ruleForm.maxWeightKg)) return "Min weight cannot be greater than max weight.";
    return "";
  }

  function reviewRule(e) {
    e.preventDefault();
    const validation = validateRule();
    if (validation) {
      setError(validation);
      return;
    }
    setError("");
    setMessage("");
    setStep(2);
  }

  async function confirmRule() {
    try {
      setLoading(true);
      setError("");
      setMessage("");
      const payload = { ...ruleForm, feedInventoryId: ruleForm.components[0].inventoryId, inventoryItemId: ruleForm.components[0].inventoryId };
      if (editingRuleId) {
        await feedingService.updateRule(editingRuleId, payload);
        setMessage("Feeding rule updated. Daily tasks will follow the saved start date.");
      } else {
        await feedingService.createRule(payload);
        setMessage("Feeding rule created. Daily tasks will be auto-created from the start date.");
      }
      clearRuleForm();
      await refreshRules();
    } catch (e) {
      setError(apiMsg(e));
    } finally {
      setLoading(false);
    }
  }

  function editRule(rule) {
    setEditingRuleId(rule.id);
    setStep(1);
    setRuleForm({
      species: rule.species || "GOAT",
      projectType: rule.projectType || "FATTENING",
      minWeightKg: rule.minWeightKg ?? "",
      maxWeightKg: rule.maxWeightKg ?? "",
      gramsPerKgPerDay: rule.gramsPerKgPerDay ?? "",
      feedingTimesPerDay: rule.feedingTimesPerDay ?? 2,
      feedInventoryId: rule.feedInventoryId ?? rule.inventoryItemId ?? "",
      components: rule.components?.length ? rule.components.map(c=>({inventoryId:String(c.inventoryId),percentage:c.percentage})) : [{inventoryId:String(rule.feedInventoryId??rule.inventoryItemId??""),percentage:100}],
      startDate: formatDate(rule.startDate) === "-" ? todayIso() : formatDate(rule.startDate),
      active: rule.active !== false,
      notes: rule.notes || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function removeRule(id) {
    if (!window.confirm("Disable this feeding rule? Existing tasks remain unchanged.")) return;
    try {
      setLoading(true);
      setError("");
      setMessage("");
      await feedingService.deleteRule(id);
      setMessage("Feeding rule disabled.");
      await refreshRules();
    } catch (e) {
      setError(apiMsg(e));
    } finally {
      setLoading(false);
    }
  }

  async function enableRule(rule) {
    if (!window.confirm("Enable this feeding rule? It must not overlap another active rule.")) return;
    try {
      setLoading(true);
      setError("");
      setMessage("");
      await feedingService.updateRule(rule.id, { active: true });
      setMessage("Feeding rule enabled.");
      await refreshRules();
    } catch (e) {
      setError(apiMsg(e));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="farm-feeding-page" style={styles.page}>
      <h2 style={styles.title}>Feeding</h2>
      <p style={styles.subtitle}>Create feeding rules once. The backend cron job creates daily pending feeding tasks from the start date. No manual generation screen is needed.</p>

      {message ? <div style={styles.success}>{message}</div> : null}
      {error ? <div style={styles.error}>{error}</div> : null}

      <Stepper step={step} />

      <section style={styles.card}>
        {step === 1 ? (
          <>
            <h3 style={styles.cardTitle}>{editingRuleId ? "Edit feeding rule" : "Define the feeding rule"}</h3>
            <p style={styles.cardText}>Set what gets fed, how much, and when feeding starts. Tasks auto-populate in the Tasks section once the rule is active.</p>

            <form onSubmit={reviewRule}>
              <div style={styles.sectionLabel}>Animal Group</div>
              <div style={styles.formGrid}>
                <label style={styles.field}>
                  <span style={styles.label}>Species</span>
                  <select style={styles.input} value={ruleForm.species} onChange={(e) => setRuleForm((s) => ({ ...s, species: e.target.value }))}>
                    {SPECIES_OPTIONS.map((item) => <option key={item} value={item}>{titleCase(item)}</option>)}
                  </select>
                </label>
                <label style={styles.field}>
                  <span style={styles.label}>Project Type</span>
                  <select style={styles.input} value={ruleForm.projectType} onChange={(e) => setRuleForm((s) => ({ ...s, projectType: e.target.value }))}>
                    {PROJECT_TYPE_OPTIONS.map((item) => <option key={item} value={item}>{titleCase(item)}</option>)}
                  </select>
                </label>
              </div>

              <div style={styles.divider} />
              <div style={styles.sectionLabel}>Weight Range <span style={styles.optional}>(optional — leave blank to apply to all weights)</span></div>
              <div style={styles.formGrid}>
                <label style={styles.field}>
                  <span style={styles.label}>Min Animal Weight (kg)</span>
                  <input style={styles.input} type="number" step="0.01" value={ruleForm.minWeightKg} onChange={(e) => setRuleForm((s) => ({ ...s, minWeightKg: e.target.value }))} placeholder="e.g. 10" />
                </label>
                <label style={styles.field}>
                  <span style={styles.label}>Max Animal Weight (kg)</span>
                  <input style={styles.input} type="number" step="0.01" value={ruleForm.maxWeightKg} onChange={(e) => setRuleForm((s) => ({ ...s, maxWeightKg: e.target.value }))} placeholder="e.g. 40" />
                </label>
              </div>

              <div style={styles.divider} />
              <div style={styles.sectionLabel}>Feed Details</div>
              <div style={styles.formGrid}>
                <label style={styles.field}>
                  <span style={styles.label}>Daily Feed Rate (g per kg of body weight)</span>
                  <input style={styles.input} type="number" min="1" step="1" value={ruleForm.gramsPerKgPerDay} onChange={(e) => setRuleForm((s) => ({ ...s, gramsPerKgPerDay: e.target.value }))} placeholder="e.g. 20" required />
                  <span style={styles.help}>Example: 20 means a 15 kg animal gets 300 g/day.</span>
                </label>
                <label style={styles.field}>
                  <span style={styles.label}>Example animal weight (kg)</span>
                  <input style={styles.input} type="number" min="0.1" step="0.1" value={previewWeight} onChange={(e) => setPreviewWeight(e.target.value)} aria-label="Example animal weight" />
                  <span style={styles.help}>Preview calculated from the complete daily ration, not an added feed allowance.</span>
                </label>
              </div>

              <div style={{...styles.card, marginTop: 14}}>
                <h4 style={{margin: "0 0 8px"}}>Feed formula components</h4>
                <p style={styles.cardText}>Every feed uses a share of the one daily ration; all percentages must add to 100%.</p>
                {ruleForm.components.map((component, index) => (
                  <div key={index} style={{display:"flex", gap:10, alignItems:"end", flexWrap:"wrap", marginBottom: 10}}>
                    <label style={{...styles.field, flex: "2 1 220px"}}>
                      <span style={styles.label}>Feed #{index + 1}</span>
                      <select aria-label={`Feed component ${index + 1}`} style={styles.input} value={component.inventoryId} onChange={(e) => setRuleForm((prev) => ({...prev, components: prev.components.map((c, i) => i === index ? {...c, inventoryId: e.target.value} : c)}))}>
                        <option value="">Select FEED inventory</option>
                        {feedInventoryOptions.map(item => <option key={item.id} value={item.id}>{item.itemName} · {item.quantity ?? 0} {item.unit ?? ""}</option>)}
                      </select>
                    </label>
                    <label style={{...styles.field, flex:"1 1 110px"}}>
                      <span style={styles.label}>Percentage</span>
                      <input aria-label={`Feed percentage ${index + 1}`} type="number" min="0.001" max="100" step="0.001" style={styles.input} value={component.percentage} onChange={(e) => setRuleForm(prev => ({...prev, components: prev.components.map((c, i) => i === index ? {...c, percentage: e.target.value} : c)}))} />
                    </label>
                    <button type="button" style={styles.ghostBtn} disabled={ruleForm.components.length <= 1} onClick={() => setRuleForm(prev => ({...prev, components: prev.components.filter((_, i) => i !== index)}))}>Remove</button>
                  </div>
                ))}
                <button type="button" style={styles.ghostBtn} onClick={() => setRuleForm(prev => ({...prev, components: [...prev.components, {inventoryId:"", percentage: ""}]}))}>+ Add feed</button>
                <p aria-live="polite" style={{marginTop:12, color: Math.abs(rationPercentageTotal - 100) < 0.000001 ? "#22c55e" : "#fbbf24"}}><b>Total: {rationPercentageTotal.toFixed(3)}%</b> {Math.abs(rationPercentageTotal-100) < 0.000001 ? "✓" : "(must equal 100%)"}</p>
                <p>Sample ration at {previewWeight || 0} kg: <strong>{previewTotalGrams.toFixed(3)} g/day</strong></p>
                {ruleForm.components.map((c, i) => {
                   const item=feedInventoryOptions.find(it => String(it.id) === String(c.inventoryId));
                   const grams=i===ruleForm.components.length-1 ? previewTotalGrams - ruleForm.components.slice(0,-1).reduce((sum,comp)=>sum+Math.round(previewTotalGrams*Number(comp.percentage||0)*10)/1000,0) : Math.round(previewTotalGrams * Number(c.percentage||0) * 10)/1000;
                   return <div key={i} style={{padding:"4px 0"}}>{item?.itemName || `Feed #${i+1}`}: {grams.toFixed(3)} g ({c.percentage || 0}%)</div>;
                })}
                <p>Estimated feed cost: <strong>{new Intl.NumberFormat("en-IN",{style:"currency",currency:"INR"}).format(estimatedDailyCost)}</strong> / goat / day (from priced stock; unpriced items excluded)</p>
              </div>

              <div style={styles.divider} />
              <div style={styles.sectionLabel}>Schedule</div>
              <label style={styles.field}>
                <span style={styles.label}>Feeding Times Per Day</span>
                <select style={styles.input} value={ruleForm.feedingTimesPerDay} onChange={e=>setRuleForm(f=>({...f,feedingTimesPerDay:Number(e.target.value)}))}>
                  <option value={2}>2 times — Morning / Evening</option>
                  <option value={3}>3 times — Morning / Afternoon / Night</option>
                </select>
                <span style={styles.help}>The daily g/kg rate is divided across sessions. Example: 45 g/kg/day → {Number(ruleForm.gramsPerKgPerDay || 0)/Number(ruleForm.feedingTimesPerDay)} g/kg per session.</span>
              </label>
              <div style={styles.formGrid}>
                <label style={styles.field}>
                  <span style={styles.label}>Feeding Start Date</span>
                  <input style={styles.input} type="date" value={ruleForm.startDate} onChange={(e) => setRuleForm((s) => ({ ...s, startDate: e.target.value }))} required />
                  <span style={styles.help}>Daily tasks will appear from this date when the rule is active.</span>
                </label>
                <label style={styles.checkRow}>
                  <input type="checkbox" checked={!!ruleForm.active} onChange={(e) => setRuleForm((s) => ({ ...s, active: e.target.checked }))} />
                  <span>Active rule</span>
                </label>
              </div>

              <label style={{ ...styles.field, marginTop: 14 }}>
                <span style={styles.label}>Notes</span>
                <textarea style={{ ...styles.input, minHeight: 86 }} value={ruleForm.notes} onChange={(e) => setRuleForm((s) => ({ ...s, notes: e.target.value }))} placeholder="Optional" />
              </label>

              <div style={styles.buttonRowRight}>
                {editingRuleId ? <button type="button" style={styles.ghostBtn} onClick={clearRuleForm}>Cancel edit</button> : null}
                <button type="submit" style={styles.primaryBtn} disabled={loading}>Review rule →</button>
              </div>
            </form>
          </>
        ) : (
          <>
            <h3 style={styles.cardTitle}>Review your feeding rule</h3>
            <p style={styles.cardText}>Everything look right? Confirm below and daily tasks will be created automatically by cron.</p>
            <div style={styles.reviewTable}>
              <div style={styles.reviewRow}><span>Species</span><b>{titleCase(ruleForm.species)}</b></div>
              <div style={styles.reviewRow}><span>Project type</span><b>{titleCase(ruleForm.projectType)}</b></div>
              <div style={styles.reviewRow}><span>Animal weight range</span><b>{ruleForm.minWeightKg || "All"} kg – {ruleForm.maxWeightKg || "All"} kg</b></div>
              <div style={styles.reviewRow}><span>Daily feed rate</span><b>{ruleForm.gramsPerKgPerDay} g per kg per day</b></div>
              <div style={styles.reviewRow}><span>Sessions daily</span><b>{ruleForm.feedingTimesPerDay} ({ruleForm.feedingTimesPerDay===3?"Morning / Afternoon / Night":"Morning / Evening"})</b></div>
              <div style={styles.reviewRow}><span>Components</span><b>{ruleForm.components.map(c=>`${feedInventoryOptions.find(i=>String(i.id)===String(c.inventoryId))?.itemName||"Feed"} ${c.percentage}%`).join(" + ")}</b></div>
              <div style={styles.reviewRow}><span>Feeding starts</span><b>{formatDate(ruleForm.startDate)}</b></div>
              <div style={styles.reviewRow}><span>Status</span><b>{ruleForm.active ? "Active" : "Inactive"}</b></div>
            </div>
            <div style={styles.infoBox}><b>What happens next:</b> Daily feeding tasks will be added to the Tasks section from the selected date. Each task stays pending until your team confirms the feed was given.</div>
            <div style={styles.buttonRowRight}>
              <button type="button" style={styles.ghostBtn} onClick={() => setStep(1)}>‹ Back</button>
              <button type="button" style={styles.greenBtn} onClick={confirmRule} disabled={loading}>✓ Confirm & create rule</button>
            </div>
          </>
        )}
      </section>

      <section style={styles.card}>
        <h3 style={styles.cardTitle}>Existing Feeding Rules</h3>
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Species</th>
                <th style={styles.th}>Project Type</th>
                <th style={styles.th}>Weight Range</th>
                <th style={styles.th}>Rate</th>
                <th style={styles.th}>Sessions</th>
                <th style={styles.th}>Feed</th>
                <th style={styles.th}>Start Date</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rules.length === 0 ? (
                <tr><td style={styles.emptyCell} colSpan={9}>No feeding rules found.</td></tr>
              ) : rules.map((r) => (
                <tr key={r.id}>
                  <td style={styles.td}>{titleCase(r.species)}</td>
                  <td style={styles.td}>{titleCase(r.projectType)}</td>
                  <td style={styles.td}>{r.minWeightKg ?? "All"} – {r.maxWeightKg ?? "All"} kg</td>
                  <td style={styles.td}>{r.gramsPerKgPerDay ?? "-"} g/kg/day</td>
                  <td style={styles.td}>{r.feedingTimesPerDay ?? 2} / day</td>
                  <td style={styles.td}>{r.components?.length ? r.components.map(c=>`${c.inventoryName||c.inventoryId} ${c.percentage}%`).join(" + ") : (r.feedInventoryName || r.feedInventoryId || "-")}</td>
                  <td style={styles.td}>{formatDate(r.startDate)}</td>
                  <td style={styles.td}><Badge status={r.active === false ? "INACTIVE" : "ACTIVE"} /></td>
                  <td style={styles.td}>
                    <div style={styles.inlineActions}>
                      <button style={styles.editBtn} onClick={() => editRule(r)}>Edit</button>
                      {r.active === false ? (
                        <button style={styles.enableBtn || styles.greenBtn} onClick={() => enableRule(r)}>Enable</button>
                      ) : (
                        <button style={styles.deleteBtn} onClick={() => removeRule(r.id)}>Disable</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

    </div>
  );
}

const styles = {
  page: { padding: 24, color: "white" },
  title: { margin: 0, fontSize: 28, fontWeight: 800 },
  subtitle: { marginTop: 8, color: "#94a3b8", lineHeight: 1.5, marginBottom: 18, maxWidth: 980 },
  success: { marginBottom: 14, padding: 14, borderRadius: 12, background: "rgba(22,163,74,0.12)", border: "1px solid rgba(22,163,74,0.35)", color: "#bbf7d0" },
  error: { marginBottom: 14, padding: 14, borderRadius: 12, background: "rgba(220,38,38,0.12)", border: "1px solid rgba(220,38,38,0.35)", color: "#fecaca" },
  card: { background: "rgba(15,23,42,0.72)", border: "1px solid rgba(148,163,184,0.18)", borderRadius: 18, padding: 22, marginBottom: 20 },
  cardTitle: { margin: 0, marginBottom: 8, fontSize: 22, fontWeight: 800 },
  cardText: { margin: "0 0 22px", color: "#94a3b8", lineHeight: 1.55 },
  stepper: { display: "flex", alignItems: "center", gap: 16, margin: "10px 0 26px" },
  stepItem: { display: "flex", alignItems: "center", gap: 10, minWidth: 160 },
  stepCircle: { width: 38, height: 38, borderRadius: 999, display: "inline-flex", alignItems: "center", justifyContent: "center", background: "rgba(30,41,59,0.8)", border: "1px solid #334155", color: "#64748b", fontWeight: 900 },
  stepActive: { background: "#6366f1", borderColor: "#818cf8", color: "white" },
  stepLabel: { color: "#64748b", fontWeight: 800 },
  stepLabelActive: { color: "#f8fafc" },
  stepLine: { flex: 1, height: 2, background: "#334155" },
  stepLineDone: { background: "#34d399" },
  sectionLabel: { margin: "20px 0 12px", color: "#818cf8", textTransform: "uppercase", letterSpacing: ".12em", fontWeight: 900, fontSize: 13 },
  optional: { textTransform: "none", letterSpacing: 0, color: "#64748b", fontWeight: 600 },
  divider: { height: 1, background: "rgba(148,163,184,0.18)", margin: "22px 0 0" },
  formGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 18 },
  filterGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 14, marginBottom: 16 },
  field: { display: "flex", flexDirection: "column", gap: 8 },
  label: { fontSize: 13, color: "#cbd5e1", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".04em" },
  input: { width: "100%", background: "#1f2937", border: "1px solid #334155", color: "white", padding: "13px 15px", borderRadius: 12, outline: "none", fontSize: 15 },
  help: { color: "#64748b", fontSize: 13, lineHeight: 1.45 },
  checkRow: { display: "flex", gap: 10, alignItems: "center", color: "#e2e8f0", marginTop: 28, fontWeight: 700 },
  infoBox: { margin: "18px 0", padding: 14, borderRadius: 12, background: "rgba(79,70,229,0.12)", border: "1px solid rgba(99,102,241,0.3)", color: "#c7d2fe", lineHeight: 1.5 },
  buttonRow: { display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" },
  buttonRowRight: { display: "flex", gap: 12, justifyContent: "flex-end", flexWrap: "wrap", alignItems: "center", marginTop: 22 },
  primaryBtn: { background: "#4f46e5", color: "white", border: "1px solid #6366f1", borderRadius: 12, padding: "12px 16px", cursor: "pointer", fontWeight: 800 },
  greenBtn: { background: "#22c55e", color: "#052e16", border: "1px solid #34d399", borderRadius: 12, padding: "12px 16px", cursor: "pointer", fontWeight: 900 },
  ghostBtn: { background: "transparent", color: "#e2e8f0", border: "1px solid #475569", borderRadius: 12, padding: "12px 16px", cursor: "pointer", fontWeight: 800 },
  reviewTable: { border: "1px solid rgba(148,163,184,0.18)", borderRadius: 14, overflow: "hidden", marginTop: 18 },
  reviewRow: { display: "flex", justifyContent: "space-between", gap: 16, padding: "15px 18px", borderBottom: "1px solid rgba(148,163,184,0.12)", color: "#94a3b8" },
  tableWrap: { overflowX: "auto", border: "1px solid rgba(148,163,184,0.16)", borderRadius: 16 },
  table: { width: "100%", borderCollapse: "collapse", minWidth: 900 },
  th: { textAlign: "left", padding: 12, background: "rgba(15,23,42,0.95)", color: "#cbd5e1", fontWeight: 800, fontSize: 13, borderBottom: "1px solid rgba(148,163,184,0.18)", whiteSpace: "nowrap" },
  td: { padding: 12, borderBottom: "1px solid rgba(148,163,184,0.12)", verticalAlign: "top", color: "#f8fafc", fontSize: 14 },
  emptyCell: { padding: 18, textAlign: "center", color: "#94a3b8" },
  badge: { display: "inline-flex", padding: "5px 10px", borderRadius: 999, fontWeight: 800, fontSize: 12 },
  inlineActions: { display: "flex", gap: 8, flexWrap: "wrap" },
  editBtn: { background: "transparent", color: "#e2e8f0", border: "1px solid #475569", borderRadius: 10, padding: "8px 10px", cursor: "pointer", fontWeight: 800 },
  deleteBtn: { background: "transparent", color: "#fca5a5", border: "1px solid #ef4444", borderRadius: 10, padding: "8px 10px", cursor: "pointer", fontWeight: 800 },
  givenBtn: { background: "#16a34a", color: "white", border: "1px solid #22c55e", borderRadius: 10, padding: "8px 10px", cursor: "pointer", fontWeight: 800 },
  notGivenBtn: { background: "#dc2626", color: "white", border: "1px solid #ef4444", borderRadius: 10, padding: "8px 10px", cursor: "pointer", fontWeight: 800 },
  skipBtn: { background: "#334155", color: "white", border: "1px solid #475569", borderRadius: 10, padding: "8px 10px", cursor: "pointer", fontWeight: 800 },
  reBtn: { background: "#7c3aed", color: "white", border: "1px solid #8b5cf6", borderRadius: 10, padding: "8px 10px", cursor: "pointer", fontWeight: 800 },
};
